import React, { useCallback, useState, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import Navbar from './components/Navbar';
import BottomNav from './components/BottomNav';
import CastMiniPlayer from './components/CastMiniPlayer';

// Lazy load heavy modal/player components (only loaded on-demand)
const Player = React.lazy(() => import('./components/Player'));
const SeriesDetails = React.lazy(() => import('./components/SeriesDetails'));
const MovieDetails = React.lazy(() => import('./components/MovieDetails'));
import { ContentProvider } from './context/ContentContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import { CastProvider } from './context/CastContext';
import AppErrorBoundary from './components/AppErrorBoundary';
import PageState from './components/PageState';
import { publicConfigError } from './lib/supabaseClient';
import './Auth.css';
import './App.css';
import './components/PageState.css';
import Footer from './components/Footer';

const loadPlayer = () => import('./components/Player');
const loadSeriesDetails = () => import('./components/SeriesDetails');
const loadMovieDetails = () => import('./components/MovieDetails');
const loadHome = () => import('./pages/Home');
const loadLandingPage = () => import('./pages/LandingPage');
const loadLoginPage = () => import('./pages/LoginPage');
const loadMovies = () => import('./pages/Movies');
const loadSeries = () => import('./pages/Series');
const loadLiveTV = () => import('./pages/LiveTV');
const loadSearch = () => import('./pages/Search');
const loadProfile = () => import('./pages/Profile');
const loadProfileSelector = () => import('./pages/ProfileSelector');
const loadMyList = () => import('./pages/MyList');
const loadOnboarding = () => import('./pages/Onboarding');
const loadAdmin = () => import('./pages/Admin');
const loadNotFoundPage = () => import('./pages/NotFoundPage');

const routePreloaders = {
  '/': loadHome,
  '/movies': loadMovies,
  '/series': loadSeries,
  '/live': loadLiveTV,
  '/search': loadSearch,
  '/profile': loadProfile,
  '/profiles': loadProfileSelector,
  '/mylist': loadMyList,
  '/admin': loadAdmin
};

const runWhenIdle = (callback) => {
  if (typeof window === 'undefined') return undefined;
  if ('requestIdleCallback' in window) {
    const id = window.requestIdleCallback(callback, { timeout: 2500 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, 600);
  return () => window.clearTimeout(id);
};

// Lazy load page components
const Home = React.lazy(loadHome);
const LandingPage = React.lazy(loadLandingPage);
const LoginPage = React.lazy(loadLoginPage);
const Movies = React.lazy(loadMovies);
const Series = React.lazy(loadSeries);
const LiveTV = React.lazy(loadLiveTV);
const Search = React.lazy(loadSearch);
const Profile = React.lazy(loadProfile);
const ProfileSelector = React.lazy(loadProfileSelector);
const MyList = React.lazy(loadMyList);
const Onboarding = React.lazy(loadOnboarding);
const Admin = React.lazy(loadAdmin);
const NotFoundPage = React.lazy(loadNotFoundPage);

const AppContent = () => {
  const { user, loading: authLoading, error: authError } = useAuth();
  const { currentProfile, profiles, loading: profileLoading, error: profileError } = useProfile();
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedMovieDetails, setSelectedMovieDetails] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const scrollPositions = useRef(new Map());

  const preloadRoute = useCallback((path) => {
    const loader = routePreloaders[path];
    if (loader) loader().catch(() => {});
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleMovieClick = useCallback((item) => {
    if (item.type === 'series_episode' && item.url) {
      setSelectedMovie(item);
      return;
    }
    // Check if it's a series (either by type prop or implicit logic)
    if (item.type === 'series' || (item.series_id && !item.url)) {
      setSelectedSeries(item);
    } else {
      setSelectedMovieDetails(item);
    }
  }, []);

  const handlePlayMovie = useCallback((movie) => {
    setSelectedMovieDetails(null);
    setSelectedMovie(movie);
  }, []);

  const closePlayer = useCallback(() => {
    setSelectedMovie(null);
  }, []);

  const handlePlayEpisode = useCallback((episode) => {
    setSelectedSeries(null); // Close series modal
    setSelectedMovie(episode); // Play episode
  }, []);

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    const positions = scrollPositions.current;
    const scrollTop = navigationType === 'POP'
      ? (positions.get(location.key) || 0)
      : 0;
    window.scrollTo({ top: scrollTop, left: 0, behavior: 'auto' });

    return () => {
      positions.set(location.key, window.scrollY);
    };
  }, [location.key, navigationType]);

  useEffect(() => {
    if (user && !profileLoading) {
      // New User -> Onboarding
      // Only redirect if NO error occurred. 
      // If error (e.g. network), do nothing or let user retry manually
      if (profiles.length === 0 && !profileError && location.pathname !== '/onboarding') {
        navigate('/onboarding');
      }
      // Existing User -> Profile Selector (if none selected)
      else if (profiles.length > 0 && !currentProfile && location.pathname !== '/profiles') {
        navigate('/profiles');
      }
    }
  }, [user, currentProfile, profiles, profileLoading, profileError, location.pathname, navigate]);

  useEffect(() => {
    if (!user) return undefined;

    // Navigation targets are tiny chunks: fetch them immediately so the first click is synchronous.
    loadHome().catch(() => {});
    loadMovies().catch(() => {});
    loadSeries().catch(() => {});
    loadLiveTV().catch(() => {});
    loadMyList().catch(() => {});

    return runWhenIdle(() => {
      loadPlayer().catch(() => {});
      loadMovieDetails().catch(() => {});
      loadSeriesDetails().catch(() => {});
      loadSearch().catch(() => {});
      loadProfile().catch(() => {});
      if (currentProfile?.role === 'admin') loadAdmin().catch(() => {});
    });
  }, [user, currentProfile?.role]);

  // Safety timeout: don't let loading block the entire app forever
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const isAppLoading = authLoading || profileLoading;

  useEffect(() => {
    if (isAppLoading) {
      const timer = window.setTimeout(() => {
        console.warn('[App] Loading safety timeout reached (12s). Force-rendering app.');
        setLoadingTimedOut(true);
      }, 12000);
      return () => window.clearTimeout(timer);
    }
    const resetTimer = window.setTimeout(() => setLoadingTimedOut(false), 0);
    return () => window.clearTimeout(resetTimer);
  }, [isAppLoading]);

  if (isAppLoading && !loadingTimedOut) {
    console.log('[App] Loading state:', { authLoading, profileLoading });
    return <div className="luma-spinner" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></div>;
  }

  if (loadingTimedOut && isAppLoading) {
    return (
      <PageState
        variant="error"
        title="Le chargement prend plus de temps que prévu"
        message="Rafraîchissez la page pour relancer la session et le chargement des profils."
        actionLabel="Recharger"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (authError && !user) {
    return (
      <PageState
        variant="error"
        title="Connexion indisponible"
        message={authError}
        actionLabel="Réessayer"
        onAction={() => window.location.reload()}
      />
    );
  }

  // Show navbar if user is logged in, except on profile selector and onboarding pages
  // Use startsWith to handle trailing slashes or URL parameters
  const showNavbar = user &&
    !location.pathname.startsWith('/profiles') &&
    !location.pathname.startsWith('/onboarding');

  return (
    <div className="app">
      {showNavbar && <Navbar onLoginClick={handleLoginClick} onPreloadRoute={preloadRoute} />}

      <div className="app-shell">
        <main className="main-content">
          <div className="route-view" key={location.pathname}>
            <Suspense fallback={<div className="route-loading" aria-label="Chargement de la page"><div className="route-loading-line" /></div>}>
              <Routes location={location}>
              {/* ... same routes ... */}
              <Route
                path="/profiles"
                element={user ? <ProfileSelector /> : <Navigate to="/" replace />}
              />

              <Route
                path="/"
                element={
                  user ? (
                    <Home
                      onMovieClick={handleMovieClick}
                      isPlayerOpen={!!selectedMovie}
                    />
                  ) : (
                    <LandingPage onLoginClick={handleLoginClick} />
                  )
                }
              />

              <Route path="/login" element={<LoginPage />} />

              <Route path="/movies" element={
                <Movies onMovieClick={handleMovieClick} />
              } />

              <Route path="/series" element={
                user ? <Series onMovieClick={handleMovieClick} /> : <LandingPage onLoginClick={handleLoginClick} />
              } />

              <Route path="/live" element={
                user ? <LiveTV onMovieClick={handleMovieClick} /> : <LandingPage onLoginClick={handleLoginClick} />
              } />

              <Route path="/search" element={
                user ? <Search onMovieClick={handleMovieClick} /> : <LandingPage onLoginClick={handleLoginClick} />
              } />

              <Route path="/profile" element={
                user ? <Profile /> : <LandingPage onLoginClick={handleLoginClick} />
              } />

              <Route path="/mylist" element={
                user ? <MyList onMovieClick={handleMovieClick} /> : <LandingPage onLoginClick={handleLoginClick} />
              } />

              <Route path="/onboarding" element={
                user ? (
                  !profileLoading && profiles.length > 0 ? <Navigate to="/profiles" replace /> : <Onboarding />
                ) : <Navigate to="/login" />
              } />

              <Route path="/admin" element={
                user ? (
                  currentProfile?.role === 'admin' ? <Admin /> : <Navigate to="/" replace />
                ) : <Navigate to="/login" />
              } />

              <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </div>
        </main>
        {showNavbar && <Footer />}
      </div>

      <Suspense fallback={null}>
        {selectedMovie && (
          <Player movie={selectedMovie} onClose={closePlayer} />
        )}

        {selectedSeries && (
          <SeriesDetails
            series={selectedSeries}
            onClose={() => setSelectedSeries(null)}
            onPlay={handlePlayEpisode}
          />
        )}

        {selectedMovieDetails && (
          <MovieDetails
            movie={selectedMovieDetails}
            onClose={() => setSelectedMovieDetails(null)}
            onPlay={handlePlayMovie}
          />
        )}
      </Suspense>

      {showNavbar && !selectedMovie && <CastMiniPlayer onOpen={setSelectedMovie} />}
      {showNavbar && <BottomNav onPreloadRoute={preloadRoute} />}
    </div>
  );
};

const App = () => {
  if (publicConfigError) {
    return (
      <PageState
        variant="fatal"
        title="Configuration publique incomplète"
        message={`${publicConfigError} Rechargez la page après correction de l'environnement.`}
        actionLabel="Recharger"
        onAction={() => window.location.reload()}
      />
    );
  }

  return (
    <AppErrorBoundary>
      <AuthProvider>
        <ProfileProvider>
          <CastProvider>
            <ContentProvider>
              <Router>
                <AppContent />
              </Router>
            </ContentProvider>
          </CastProvider>
        </ProfileProvider>
      </AuthProvider>
    </AppErrorBoundary>
  );
};

export default App;
